import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { Panel, PanelBody, PanelFoot, PanelHead } from '../../components/primitives/Panel';
import { browseMessages as messages } from '../browseMessages';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './BrowseRows.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface BrowseResultPanelProps {
  title: string;
  icon: React.ReactNode;
  total: number;
  totalIsCapped: boolean;
  shown: number;
  /** One more line in the head, for example "Opening a playlist is free." */
  note?: string;
  /** The All tab: "See all N" switches to this type. */
  onSeeAll?: () => void;
  /** One type: "Show more" asks the server for a longer page. */
  onShowMore?: () => void;
  /** The column head row, drawn above the rows. */
  columns?: React.ReactNode;
  children: React.ReactNode;
}

/** One result panel (design a1 Artists, Tracks, Playlists). */
const BrowseResultPanelComponent: React.FC<BrowseResultPanelProps> = ({
  title,
  icon,
  total,
  totalIsCapped,
  shown,
  note,
  onSeeAll,
  onShowMore,
  columns,
  children,
}) => {
  const intl = useIntl();
  const count = totalIsCapped
    ? intl.formatMessage(messages.resultsCapped, { count: total })
    : intl.formatMessage(messages.results, { count: total });
  const totalLabel = totalIsCapped ? `${total}+` : String(total);

  return (
    <Panel as="section">
      <PanelHead
        title={title}
        icon={icon}
        subtitle={note === undefined ? count : `${count}. ${note}`}
        right={
          onSeeAll !== undefined && total > shown ? (
            <button type="button" className={styles.linkButton} onClick={onSeeAll}>
              {intl.formatMessage(messages.seeAll, { count: totalLabel })}
            </button>
          ) : undefined
        }
      />
      <PanelBody flush>
        {columns}
        {children}
      </PanelBody>
      {onShowMore !== undefined && total > shown && (
        <PanelFoot>
          <span className={styles.showMore}>
            <span>{intl.formatMessage(messages.shownOf, { shown, total: totalLabel })}</span>
            <button type="button" className={styles.linkButton} onClick={onShowMore}>
              {intl.formatMessage(messages.showMore)}
            </button>
          </span>
        </PanelFoot>
      )}
    </Panel>
  );
};

export const BrowseResultPanel = TypedMemo(BrowseResultPanelComponent);
